import { useState, useEffect } from 'react';
import { useDataApi } from '@adsk/alloy-helpers';
import { useLocale } from '@adsk/alloy-react-locale';
import { useCurrentProjectId } from '@adsk/alloy-provider';
import { useFormikContext } from 'formik';
import { SUBMITTALS_PERMISSION_TYPES } from '../constants';
import {
  buildGetSubmittalsPermissionsRequest,
  getSubmittalsPermissionsInfo,
} from './utils';

const useSubmittalsPermissions = (buildMemberAccess) => {
  const [permissionsInfo, setPermissionsInfo] = useState({ loading: true });
  const { values } = useFormikContext();
  const {
    member: { autodeskId: memberAutodeskId, selectedCompany, selectedRoles },
  } = values;
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  const projectId = useCurrentProjectId();
  const {
    data: submittalsPermissionsData,
    doFetch: doFetchSubmittalsPermissions,
    loading: isLoadingPermissionsData,
    error: submittalsPermissionsError,
    setData: setSubmittalsPermissionsData,
  } = useDataApi();

  useEffect(() => {
    if (!projectId || !buildMemberAccess) return;

    if (!memberAutodeskId && !selectedCompany && !selectedRoles) {
      setSubmittalsPermissionsData({ results: [] });
      return;
    }

    doFetchSubmittalsPermissions(
      buildGetSubmittalsPermissionsRequest(
        projectId,
        memberAutodeskId,
        selectedCompany?.memberGroupId,
        selectedRoles ? selectedRoles.map((e) => e.memberGroupId) : null
      )
    );
  }, [
    projectId,
    memberAutodeskId,
    selectedCompany,
    selectedRoles,
    buildMemberAccess,
    doFetchSubmittalsPermissions,
    setSubmittalsPermissionsData,
  ]);

  useEffect(() => {
    if (!submittalsPermissionsData) return;

    const { results } = submittalsPermissionsData;

    const memberPermissions = memberAutodeskId
      ? results.find(
          (permission) =>
            permission.oxygenId === memberAutodeskId &&
            permission.userType === SUBMITTALS_PERMISSION_TYPES.USER
        )
      : null;
    const companyPermissions = selectedCompany
      ? results.find(
          (permission) =>
            permission.oxygenId === selectedCompany.memberGroupId &&
            permission.userType === SUBMITTALS_PERMISSION_TYPES.COMPANY
        )
      : null;
    const rolePermissions = selectedRoles
      ? results.filter(
          (permission) =>
            selectedRoles.some(
              (r) => r.memberGroupId === permission.oxygenId
            ) && permission.userType === SUBMITTALS_PERMISSION_TYPES.ROLE
        )
      : null;
    const info = getSubmittalsPermissionsInfo(
      memberPermissions,
      companyPermissions,
      rolePermissions,
      selectedCompany,
      selectedRoles,
      T
    );

    setPermissionsInfo(info);
  }, [
    submittalsPermissionsData,
    memberAutodeskId,
    selectedCompany,
    selectedRoles,
    T,
  ]);

  useEffect(() => {
    if (isLoadingPermissionsData) setPermissionsInfo({ loading: true });
  }, [isLoadingPermissionsData]);

  useEffect(() => {
    if (!submittalsPermissionsError) return;

    setPermissionsInfo({});
  }, [submittalsPermissionsError]);

  return permissionsInfo;
};

export default useSubmittalsPermissions;
